import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://brightonenglishcenter.edu.co";
const SITE_NAME = "Brighton Centro de Inglés Pasto";

const defaultMeta = {
  title: `${SITE_NAME} | Certificaciones & Cursos de Inglés`,
  description:
    "Brighton Centro de Inglés en Pasto, Nariño. Cursos de inglés para niños, jóvenes y adultos y preparación para certificaciones internacionales como IELTS, TOEFL iBT y APTIS.",
  image: `${SITE_URL}/img/grado_2024.webp`,
  keywords:
    "cursos de inglés en Pasto, instituto de inglés en Pasto, academia de inglés en Pasto, inglés para niños en Pasto, IELTS en Pasto, TOEFL iBT en Pasto, APTIS en Pasto, OOPT",
};

function normalizePath(pathname = "") {
  if (!pathname || pathname === "/") return "/";
  return `/${pathname.replace(/^\/+|\/+$/g, "")}`;
}

function buildUrl(pathname = "") {
  const path = normalizePath(pathname);
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export default function Seo({
  title,
  description,
  pathname = "",
  image,
  children,
  noindex = false,
  canonical,
  keywords,
  type = "website",
}) {
  const path = normalizePath(pathname);
  const url = buildUrl(path);
  const canonicalUrl = canonical || url;

  const metaTitle = title
    ? `${title} | ${SITE_NAME}`
    : defaultMeta.title;

  const metaDescription = description || defaultMeta.description;
  const metaImage = image || defaultMeta.image;
  const metaKeywords = keywords || defaultMeta.keywords;

  const robots = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large";

  return (
    <Helmet>
      <html lang="es-CO" />

      <title>{metaTitle}</title>

      <meta name="description" content={metaDescription} />
      <meta name="robots" content={robots} />

      {/* Keywords no es una señal de posicionamiento de Google.
          Se conserva opcionalmente por compatibilidad con otros sistemas. */}
      {metaKeywords && (
        <meta name="keywords" content={metaKeywords} />
      )}

      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:locale" content="es_CO" />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/webp" />
      <meta
        property="og:image:alt"
        content={`${SITE_NAME} - Cursos de inglés y certificaciones internacionales`}
      />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      <meta
        name="twitter:image:alt"
        content={`${SITE_NAME} - Cursos de inglés`}
      />

      {children}
    </Helmet>
  );
}